import uniqBy from 'lodash/uniqBy';
import isArray from 'lodash/isArray';
import { generateCheckedItemsInRange, isCheckedResultInRange } from '@Containers/PatentFamilyClustering/utils';
import {
  INIT_RESULTS_STATE,
  SET_CHECKED_ITEMS,
  SET_RESULTS_PER_PAGE,
  FETCH_RESULTS_ITEMS,
  FETCH_RESULTS_ITEMS_SUCCESS,
  FETCH_RESULTS_ITEMS_FAILURE,
  ADD_RESULTS_CHUNK,
  SET_ALL_RESULTS_CHECKED,
  SET_ALL_RESULTS_UNCHECKED,
  SET_RESULT_CHECKED,
  SET_PRODUCT_CHECKED,
  SET_PRODUCTS_CHECKED_FOR_SUBSTANCE,
  CLEAN_RESULTS_STATE,
} from './constants';
import { mapResultItemToChecked, mapProductToChecked, mapAllResultsToChecked } from './utils';

export const initialState = {
  resultsPerPage: null,
  resultsFinished: false,
  resultsInProgress: 0,
  resultsItems: [],
  requestedParams: {}, // for last loaded results
  checkedItems: {
    results: [], // expected item format: { index, id }, additionally for patent family clustering: { absoluteIndex, relativeIndex }
    products: [], // expect item format: { productIndex, productId, substanceIndex, substanceId }
  },
};

function handleInitResultsState(state, payload) {
  const { params, checkedItems } = payload;
  return {
    ...state,
    requestedParams: params,
    checkedItems: checkedItems ?? initialState.checkedItems,
  };
}

function handleSetCheckedItems(state, payload) {
  const { checkedItems } = payload;
  if (!checkedItems) {
    return state;
  }
  return { ...state, checkedItems };
}

function handleFetchResultsItemsDone(state) {
  return {
    ...state,
    resultsInProgress: state.resultsInProgress > 1 ? state.resultsInProgress - 1 : 0,
    resultsFinished: state.resultsInProgress <= 1,
  };
}

function isResultAlreadyChecked(checkedResult, mappedResultsToChecked) {
  const { index: checkedResultIndex, absoluteIndex: checkedResultAbsoluteIndex } = checkedResult;

  // Patent Family Clustering
  if (checkedResultAbsoluteIndex) {
    return mappedResultsToChecked.some(({ absoluteIndex }) => absoluteIndex === checkedResultAbsoluteIndex);
  }

  // Generic results
  return mappedResultsToChecked.some(({ index }) => index === checkedResultIndex);
}

function handleSetAllResultsChecked(state, payload) {
  const { checked } = payload;
  const { resultsItems, checkedItems } = state;
  const { results: checkedResults, products: checkedProducts } = checkedItems;
  const mappedResultsToChecked = mapAllResultsToChecked(resultsItems);

  return {
    ...state,
    checkedItems: {
      ...checkedItems,
      results: checkedResults
        .filter((checkedResult) => !isResultAlreadyChecked(checkedResult, mappedResultsToChecked))
        .concat(checked ? mappedResultsToChecked : []),
      products: checkedProducts.filter(({ substanceIndex }) => !resultsItems.some(({ index }) => index === substanceIndex)),
    },
  };
}

function resolveNewCheckedResults(checkedResults, item, checked) {
  const { index, absoluteIndex } = item;

  if (!absoluteIndex) {
    // Generic results
    return checkedResults
      .filter(({ index: checkedResultIndex }) => checkedResultIndex !== index)
      .concat(checked ? [mapResultItemToChecked(item)] : []);
  }

  if (isArray(absoluteIndex)) {
    // Patent Family Clustering - main result item
    return checkedResults
      .filter((checkedResult) => !isCheckedResultInRange(checkedResult, absoluteIndex))
      .concat(checked ? generateCheckedItemsInRange(absoluteIndex, index) : []);
  }

  // Patent Family Clustering - single patents or family members
  return checkedResults
    .filter(({ absoluteIndex: checkedResultAbsoluteIndex }) => checkedResultAbsoluteIndex !== absoluteIndex)
    .concat(checked ? [mapResultItemToChecked(item)] : []);
}

function handleSetResultChecked(state, payload) {
  const { item, checked } = payload;
  const { index } = item;
  const { checkedItems } = state;
  const { results: checkedResults, products: checkedProducts } = checkedItems;

  return {
    ...state,
    checkedItems: {
      ...checkedItems,
      results: resolveNewCheckedResults(checkedResults, item, checked),
      products: checkedProducts.filter(({ substanceIndex }) => substanceIndex !== index),
    },
  };
}

function handleSetProductChecked(state, payload) {
  const { product, checked } = payload;
  const { productIndex } = product;
  const { checkedItems } = state;
  const { products: checkedProducts } = checkedItems;

  return {
    ...state,
    checkedItems: {
      ...checkedItems,
      products: checkedProducts
        .filter(({ productIndex: checkedProductIndex }) => checkedProductIndex !== productIndex)
        .concat(checked ? [mapProductToChecked(product)] : []),
    },
  };
}

function handleSetProductsCheckedForSubstance(state, payload) {
  const { products, substanceIndex } = payload;
  const { checkedItems } = state;
  const { results: checkedResults, products: checkedProducts } = checkedItems;

  return {
    ...state,
    checkedItems: {
      ...checkedItems,
      results: checkedResults.filter(({ index }) => index !== substanceIndex),
      products: checkedProducts
        .filter(({ substanceIndex: checkedSubstanceIndex }) => checkedSubstanceIndex !== substanceIndex)
        .concat(products.map((product) => mapProductToChecked(product))),
    },
  };
}

// eslint-disable-next-line default-param-last
export default function reducer(state = initialState, action) {
  const { type, payload } = action;
  switch (type) {
    case INIT_RESULTS_STATE:
      return handleInitResultsState(state, payload);
    case SET_CHECKED_ITEMS:
      return handleSetCheckedItems(state, payload);
    case CLEAN_RESULTS_STATE:
      return {
        ...state,
        resultsItems: initialState.resultsItems,
        totalResults: initialState.totalResults,
        resultsFinished: false,
        checkedItems: initialState.checkedItems,
      };
    case SET_RESULTS_PER_PAGE:
      return { ...state, resultsPerPage: payload };
    case FETCH_RESULTS_ITEMS:
      return { ...state, resultsItems: [], resultsInProgress: state.resultsInProgress + 1, resultsFinished: false };
    case FETCH_RESULTS_ITEMS_SUCCESS:
    case FETCH_RESULTS_ITEMS_FAILURE:
      return handleFetchResultsItemsDone(state);
    case ADD_RESULTS_CHUNK:
      return { ...state, resultsItems: uniqBy(state.resultsItems.concat(payload), ({ index }) => index) };
    case SET_ALL_RESULTS_CHECKED:
      return handleSetAllResultsChecked(state, payload);
    case SET_ALL_RESULTS_UNCHECKED:
      return { ...state, checkedItems: initialState.checkedItems };
    case SET_RESULT_CHECKED:
      return handleSetResultChecked(state, payload);
    case SET_PRODUCT_CHECKED:
      return handleSetProductChecked(state, payload);
    case SET_PRODUCTS_CHECKED_FOR_SUBSTANCE:
      return handleSetProductsCheckedForSubstance(state, payload);
    default:
      return state;
  }
}
